import { RELATIONSHIP_SELF } from '@steward-app/steward-types';
import type { PersonAttributes } from '../api/types';

/**
 * What a person record holds, as a sentence (steward-app#1425).
 *
 * ⚠️ **Prose, never a coloured badge, and never a completeness score.** `DESIGN.md` forbids
 * green/red status badges, and `src/advisor/intakeStatus.ts` restates the reason a roster is
 * the exact place that invites one: none of these states is good or bad news. A household
 * that has written down a four-year-old's name and nothing else has not failed at anything,
 * and a percentage or an amber dot would tell them they had. So this says what IS there and
 * stops, in the family's own words.
 *
 * It is deliberately a pure function over the attributes, with no notion of "required"
 * beyond what the type itself enforces (`legalName`). Adding a "missing" list here is how
 * this becomes a nag; the onboarding request artifact and the nudge layers are separate
 * work (steward-app#1426/#1427) with their own designs, and they are the place for anything
 * that ASKS for a field.
 */

/** The optional details the roster summarises, in the order a person would say them. */
const DETAILS: readonly { key: keyof PersonAttributes; label: string }[] = [
  { key: 'dateOfBirth',      label: 'date of birth' },
  { key: 'relationship',     label: 'relationship' },
  { key: 'phone',            label: 'phone' },
  { key: 'email',            label: 'email' },
  { key: 'legalAddress',     label: 'legal address' },
  { key: 'mailingAddress',   label: 'mailing address' },
  { key: 'trustedContactId', label: 'trusted contact' },
];

function present(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/** `['a']` → `'a'`; `['a','b']` → `'a and b'`; `['a','b','c']` → `'a, b, and c'`. */
function listWords(words: readonly string[]): string {
  if (words.length === 1) return words[0]!;
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  return `${words.slice(0, -1).join(', ')}, and ${words[words.length - 1]}`;
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * One line naming what this record holds beyond the legal name.
 *
 * `'Name only'` when there is nothing else, `'Date of birth only'` when there is exactly
 * one thing, and a plain list otherwise. Never empty, so the column never renders a gap the
 * reader has to interpret.
 */
export function personRecordSummary(attributes: PersonAttributes): string {
  const held = DETAILS.filter(d => present(attributes[d.key])).map(d => d.label);
  if (held.length === 0) return 'Name only';
  if (held.length === 1) return `${sentenceCase(held[0]!)} only`;
  return sentenceCase(listWords(held));
}

/**
 * The name the roster shows: the preferred name when the household gave one, else the legal
 * name (which is the only field the type requires, and so the only safe fallback).
 *
 * The legal name is still shown beside it when the two differ — naming somebody one way in
 * a list and another way on their record is how a roster stops being recognisable.
 *
 * ⚠️ **TOTAL over any attribute bag, including a malformed one, and that is load-bearing
 * rather than defensive habit.** One of its three callers is `src/pages/people/Access.tsx`
 * — the one surface a locked-out family has to be able to reach — where a throw in here takes
 * the invite form, the role picker and the remove control down with it. It used to read
 * `attributes.preferredName?.trim()`, which throws on a non-string `preferredName` (`?.`
 * guards null and undefined, not a number), so the field's TYPE was doing the work and the
 * type is a claim about a server response, not a fact about one. A bag with nothing usable
 * yields `''`, which renders as an empty name rather than as a blank page.
 * (steward-app#1425 review.)
 */
export function personDisplayName(attributes: PersonAttributes): string {
  const preferred = typeof attributes.preferredName === 'string' ? attributes.preferredName.trim() : '';
  if (preferred) return preferred;
  return typeof attributes.legalName === 'string' ? attributes.legalName.trim() : '';
}

/**
 * The `legalName` a person-identity write should send, joined from the split fields the
 * People form and `PersonDetail`'s edit form collect (steward-app#1564).
 *
 * `legalName` stays the `person` type's one required field, and stays client-derived rather
 * than appcore `derived: true` (see {@link PersonAttributes.legalName}'s own doc for why) —
 * so every write that touches identity must compute and send it itself. This is the one
 * place that does, so both forms produce the same join.
 *
 * Trims each half before joining and the result after, so a blank last name (a form left
 * with only a first name) never leaves a trailing space, matching the join
 * `intakePrefill.ts` already uses for the same two fields.
 *
 * ⚠️ **TOTAL over a malformed argument, for the same reason {@link personDisplayName} is.**
 * `PersonForm` seeds its `firstName` box straight from a server bag
 * (`initial.firstName ?? initial.legalName`), and this function's own first caller runs that
 * value through here before the write ever leaves the browser. `?.` guards null and
 * undefined, not a number or object, so a plain `firstName?.trim()` would throw on a
 * malformed response instead of falling back to `''` — the exact hazard #1425's review found
 * in `attributes.preferredName?.trim()` (see that doc), reintroduced here if left unguarded.
 * `typeof x === 'string'` closes it the same way. (steward-app#1564 review round 2.)
 */
export function deriveLegalName(firstName: string | undefined, lastName: string | undefined): string {
  const first = typeof firstName === 'string' ? firstName.trim() : '';
  const last = typeof lastName === 'string' ? lastName.trim() : '';
  return `${first} ${last}`.trim();
}

/** `person.memberId`: declared with the type (PR #1439), removed from the type by steward-app#1851. No code
 *  in this repo or appcore wrote it (grep of both, 2026-10-01), so no stored row is expected
 *  to carry it; it is stripped anyway because a row that did would fail strict validation on
 *  every edit that sent it back. The one member of the strip list the pack does not declare. */
export const LEGACY_MEMBER_ID_KEY = 'memberId';

/** `taxIdHint`, appcore's six `ENCRYPTED_FIELD_KEYS`, the two `createOnly` onboarding
 *  stamps (crossmarc/appcore#739), the `routeOnly` self link (`linkedUserId`,
 *  steward-app#1851), and `memberId`, the field `linkedUserId` replaced. Exported for the
 *  test that pins {@link personWriteAttributes} against the type declaration. */
export const SERVER_OWNED_PERSON_KEYS: readonly string[] = [
  'taxIdHint',
  'secretCiphertext', 'encryptedDataKey', 'iv', 'authTag', 'keyId', 'scheme',
  'onboardingSubject', 'onboardingSource',
  'linkedUserId',
  LEGACY_MEMBER_ID_KEY,
];

/**
 * Every attribute appcore REFUSES from a client on the ordinary person write, stripped.
 *
 * ⚠️ **This is not tidying. Without it, every person edit 400s.** (steward-app#1260)
 *
 * `taxIdHint` is declared `derived: true` on the `person` type, and the six envelope keys
 * are declared `sensitive: true`. appcore answers a client-supplied value for either class
 * with a 400 on its generic create and update alike — `DERIVED_ATTRIBUTES` for the hint —
 * whatever capabilities the caller holds. Both classes are carried forward when a write
 * OMITS them, so omitting is not merely the safe option, it is the only one that means
 * "leave it alone".
 *
 * Both of this page's write paths are read-modify-write over the whole attribute bag: the
 * edit form spreads `...initial`, and the annotations panel spreads `...at`. Both therefore
 * send back whatever the GET handed them, which after #1260 includes `taxIdHint` for any
 * person with a number on file. Neither call has anything to do with the tax id, so the bug
 * would be: saving a note on the one person whose SSN is recorded fails with a validation
 * error naming a field the form does not show.
 *
 * The two onboarding stamps (`onboardingSubject`, `onboardingSource`, crossmarc/appcore#739)
 * are the third class, and unlike the envelope they ARE present on reads: the client prefill
 * writes them at create on the client's and spouse's rows, and the silent tax-id copy
 * (`src/onboarding/client/taxIdCopy.ts`) finds its target by them. They are `createOnly`, so
 * appcore 400s `CREATE_ONLY_ATTRIBUTES` on any member write that carries one, and the façade
 * refuses them too (`router.ts` `refuseAttributes`). Omitted, an appcore that knows the flag
 * carries them forward (an older one drops them, which only loses the copy target). Without
 * them here, every edit of a prefilled client or spouse would fail.
 *
 * `linkedUserId` (steward-app#1851) is the fourth class, `routeOnly`: present on reads (as a
 * fixed marker from the façade, never the stored id), written
 * only by appcore's self-link routes, refused on the generic plane when present (400
 * `ROUTE_ONLY_ATTRIBUTES`) and carried forward when omitted. Without it here, every edit of a
 * linked person would fail. The façade drops it from a generic write too (`router.ts`
 * `dropInputAttributes`).
 *
 * The envelope keys are in the list for completeness and are expected never to be present:
 * appcore strips them from every read, and `router.ts`'s `stripAttributes` strips them
 * again. Stripping a key that is already absent costs nothing, and the list is the place a
 * reader looks to learn which fields are server-owned.
 *
 * Deliberately a DENYLIST over the bag rather than an allowlist of the ten known fields.
 * An allowlist would silently drop any attribute a future release adds before this function
 * learns about it, which is the same class of data loss in the opposite direction. The
 * denylist is safe here because its members are exactly the fields appcore declares
 * server-owned, and `personRecord.test.ts` joins this list to the `person` type declaration
 * (`sensitive`, `derived`, `createOnly` or `routeOnly`) in both directions, with
 * {@link LEGACY_MEMBER_ID_KEY} as the one named exception, so a field of any of those classes
 * cannot be added to the pack without this list being updated to match.
 */
export function personWriteAttributes(attributes: PersonAttributes): PersonAttributes {
  const out = { ...attributes } as Record<string, unknown>;
  for (const key of SERVER_OWNED_PERSON_KEYS) delete out[key];
  return out as unknown as PersonAttributes;
}

/**
 * Whether this record is linked to a login. The façade sends a fixed marker in
 * `linkedUserId` when it is and omits the key when it is not (`PersonAttributes.linkedUserId`),
 * so this says THAT a record is linked and never to whom. Never an access check. Total over a
 * malformed bag for the reason {@link personDisplayName} is: a non-string or blank value
 * reads as unlinked.
 */
export function personIsLinked(attributes: PersonAttributes): boolean {
  const v = (attributes as { linkedUserId?: unknown }).linkedUserId;
  return typeof v === 'string' && v.trim() !== '';
}

/**
 * Whether the relationship reads as Self (compared trimmed and case-insensitively against
 * `RELATIONSHIP_SELF`). Used only as a fallback that withholds "Invite to sign in" from that
 * row while the viewer has no linked record of their own (steward-app#1977, narrowed by
 * #1851; `personSelfLink.ts` `selfLinkRowState` holds the rule). `relationship` is free text
 * anyone can choose, so this is a proxy for "the record of the person who set this workspace
 * up", not proof the row is linked to a login: callers must never show "Manage access" or
 * "You" on the strength of it. A row that does carry a `linkedUserId` is decided by
 * {@link personIsLinked} first. Total on a non-string `relationship`, like its neighbours.
 */
export function personRelationshipIsSelf(attributes: PersonAttributes): boolean {
  const r = attributes.relationship;
  return typeof r === 'string' && r.trim().toLowerCase() === RELATIONSHIP_SELF.toLowerCase();
}
